import React, { useEffect, useState } from "react";
import { Alert, Button, message } from "antd";
import moment from "moment";
import SubscribeButton from "./SubscribeButton";
import { observer } from "mobx-react-lite";
import { PlatformMarketShopTypes, hasSupportSubscribeShop } from "@/utils/ShopUtils";
import { useGlobalStore } from "@/store/hooks";
import AutoLoadingButton from "@/components/AutoLoadingButton";

interface ShopExpiredAlertProps {
    shopId: number;
}

/**
 * 发货易过期时间通知组件
 */
const ShopExpiredAlert: React.FC<ShopExpiredAlertProps> = function (props) {
    const { shopId } = props;
    // 剩余天数（只算整天差，不包含小时、分钟）
    const [remainingDays, setRemainingDays] = useState<number>();
    const globalStore = useGlobalStore();
    const shopSetting = globalStore.shopSettingMapping.get(shopId);
    // 判断当前店铺类型是否支持续费订阅
    const supportSubscribeShop = hasSupportSubscribeShop(shopSetting?.type);

    const onRunSyncSuccess = async () => {
        // 同步成功后，重新拉取最新的店铺设置
        await globalStore.getShopSetting([shopSetting.id]);
        message.success("更新到期时间成功");
    };

    // 当 shopSetting.subscriptionEndTime 更新时，重新计算 remainingDays
    useEffect(() => {
        if (!shopSetting) {
            setRemainingDays(undefined);
            return;
        }
        // 如果没有到期时间且支持续费，主动去拉一次
        if (!shopSetting.subscriptionEndTime && supportSubscribeShop) {
            globalStore.appSubscriptionStore.syncShopSubscription(shopSetting.id).then(onRunSyncSuccess);
            setRemainingDays(undefined);
            return;
        }
        if (shopSetting.subscriptionEndTime) {
            const now = moment();
            const end = moment(shopSetting.subscriptionEndTime);
            // 只比较日期部分（从今天零点到到期日期零点的差）
            const diffDays = end.startOf("day").diff(now.startOf("day"), "days");
            setRemainingDays(diffDays);

            if (now.isAfter(end)) {
                // 店铺已过期，主动同步
                globalStore.appSubscriptionStore.syncShopSubscription(shopSetting.id).then(() => {
                    globalStore.getShopSetting([shopSetting.id]);
                });
            }
        } else {
            setRemainingDays(undefined);
        }
    }, [shopSetting?.subscriptionEndTime, shopSetting?.id, supportSubscribeShop]);

    async function handleUpdateExpiredDate(e) {
        await globalStore.appSubscriptionStore.syncShopSubscription(shopSetting.id);

        onRunSyncSuccess();
    }

    // 如果没有配置或不支持续费，直接不展示
    if (!shopSetting || !supportSubscribeShop) {
        return null;
    }

    // 用精确时刻判断是否已过期
    const now = moment();
    const end = moment(shopSetting.subscriptionEndTime);
    let tip = "";

    if (now.isAfter(end)) {
        // 当前时间已超过到期精确时间点
        tip = "已过期，请续费。";
    } else if (now.isSame(end, "day")) {
        // 同一天内但还没到具体过期时刻
        tip = "将在今天到期，请及时续费以免漏单。";
    } else if (remainingDays !== undefined && remainingDays <= 7) {
        // 7天内到期提示
        tip = `还有${remainingDays}天到期，请及时续费以免漏单。`;
    }

    if (!tip) {
        // 其他情况不展示任何提醒
        return null;
    }

    const confirmTip = PlatformMarketShopTypes.includes(shopSetting.type) ? (
        <span className="error">（温馨提示： 请核对跳转的服务市场上登录的右上角账号 以防续错店铺）</span>
    ) : null;

    const messageEl = (
        <div>
            <span>
                该店铺【{shopSetting.name}】，{tip}
                {confirmTip}
                订购后点击这里：
            </span>
            <AutoLoadingButton className="mr10" type="link" onClick={handleUpdateExpiredDate}>
                更新到期时间
            </AutoLoadingButton>
            <SubscribeButton shopSetting={shopSetting} />
        </div>
    );

    return <Alert className="mb10" type="error" title={messageEl} />;
};

export default observer(ShopExpiredAlert);
